import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hostingService, HostingPackage } from '../services/hostingService';
import { FaCheck, FaArrowRight, FaServer, FaGlobe, FaShoppingCart } from 'react-icons/fa';

interface TransferData {
  domainName: string;
  authCode: string;
  contactInfo: any;
  transferPrice: number;
}

const DomainTransferHosting: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [transferData, setTransferData] = useState<TransferData | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('domain-only');
  const [hostingPlans, setHostingPlans] = useState<any[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [planError, setPlanError] = useState('');

  useEffect(() => {
    // Scroll to top of page when component mounts
    window.scrollTo(0, 0);

    // Check if user is authenticated
    if (!user && !localStorage.getItem('token')) {
      navigate('/login?message=Please sign in to continue with domain transfer');
      return;
    }

    // Get transfer data from navigation state
    if (location.state?.transferData) {
      setTransferData(location.state.transferData);
    } else {
      // Redirect back to domain transfer if no data
      navigate('/domain-transfer');
    }

    // Fetch hosting plans from database
    fetchHostingPlans();
  }, [user, location.state, navigate]);

  const fetchHostingPlans = async () => {
    try {
      setLoadingPlans(true);
      console.log('🔍 Fetching hosting plans from database...');
      
      const result = await hostingService.getPackages();
      console.log('📡 Hosting service result:', result);
      
      if (result.success && result.data && result.data.length > 0) {
        // Filter only shared hosting packages that are active - using correct field names
        const hostingPackages = result.data
          .filter((pkg: HostingPackage) => pkg.type === 'shared' && pkg.status === 'active')
          .slice(0, 3); // Only show first 3 plans
        
        console.log('✅ Filtered hosting packages:', hostingPackages);
        
        if (hostingPackages.length > 0) {
          setHostingPlans(hostingPackages);
          console.log('✅ Using database hosting plans');
          return;
        }
      }
      
      console.log('⚠️ No valid hosting packages found in database');
      setHostingPlans([]);
    } catch (error: any) {
      console.error('❌ Error fetching hosting plans:', error);
      setHostingPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const calculateTotal = () => {
    if (!transferData) return 0;
    
    if (selectedPlan === 'domain-only') {
      return transferData.transferPrice;
    }
    
    const plan = hostingPlans.find((p: any) => p.id === selectedPlan);
    if (plan) {
      return transferData.transferPrice + parseFloat(plan.price_monthly || plan.price || '0');
    }
    
    return transferData.transferPrice;
  };

  const handleContinueToCheckout = () => {
    if (!transferData) return;

    const checkoutData = {
      domain: {
        name: transferData.domainName,
        price: transferData.transferPrice,
        type: 'transfer'
      },
      hosting: selectedPlan !== 'domain-only' ? {
        plan: hostingPlans.find((p: any) => p.id === selectedPlan),
        planId: selectedPlan
      } : null,
      contactInfo: transferData.contactInfo,
      authCode: transferData.authCode,
      total: calculateTotal()
    };

    navigate('/checkout/domain-transfer', { state: { checkoutData } });
  };

  if (!transferData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading...</h1>
            <p className="text-gray-600">Please wait while we load your transfer information.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Header */}
      <section className="bg-white border-b border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 mb-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">Step 2 of 3</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-black mb-4 text-gray-900">
              Complete Your Setup for
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 block">
                {transferData.domainName}
              </span>
            </h1>
            
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose a hosting plan for your domain or continue with domain transfer only
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Domain Only Option */}
          <div className="mb-8">
            <div 
              className={`bg-gradient-to-br from-white via-gray-50 to-gray-100 border-2 rounded-2xl p-6 shadow-lg cursor-pointer transition-all duration-200 relative overflow-hidden ${
                selectedPlan === 'domain-only' 
                  ? 'border-orange-400 ring-4 ring-orange-100' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedPlan('domain-only')}
            >
              {selectedPlan === 'domain-only' && (
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                    <FaCheck className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mr-4">
                    <FaGlobe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Domain Transfer Only</h3>
                    <p className="text-gray-600">Transfer your domain without hosting</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">${transferData.transferPrice.toFixed(2)}</div>
                  <div className="text-sm text-gray-500">one-time + 1 year extension</div>
                </div>
              </div>
            </div>
          </div>

          {/* Hosting Plans */}
          <div className="mb-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Or Choose a Hosting Plan</h2>
              <p className="text-gray-600">Get hosting for your domain with these popular plans</p>
            </div>

            {loadingPlans ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading hosting plans...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {hostingPlans.map((plan: any) => (
                  <div 
                    key={plan.id}
                    className={`bg-gradient-to-br from-white via-gray-50 to-gray-100 border-2 rounded-2xl p-8 shadow-lg cursor-pointer transition-all duration-200 relative overflow-hidden ${
                      selectedPlan === plan.id 
                        ? 'border-orange-400 ring-4 ring-orange-100 transform scale-105' 
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-xl'
                    }`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {/* Glossy overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>

                    {selectedPlan === plan.id && (
                      <div className="absolute top-4 right-4">
                        <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                          <FaCheck className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    )}
                    
                    <div className="relative z-10">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                          <FaServer className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <div className="text-center">
                          <span className="text-3xl font-black text-gray-900">${parseFloat(plan.price_monthly || plan.price || '0').toFixed(2)}</span>
                          <span className="text-gray-600">/month</span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-6">
                        {(plan.features && plan.features.length > 0 ? plan.features : ['No features available']).map((feature: string, index: number) => (
                          <div key={index} className="flex items-center">
                            <FaCheck className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 text-sm">
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div><strong>Storage:</strong> {plan.storage_gb || 'N/A'}GB</div>
                          <div><strong>Bandwidth:</strong> {plan.bandwidth_gb === 0 ? 'Unlimited' : `${plan.bandwidth_gb || 'N/A'}GB`}</div>
                          <div><strong>Websites:</strong> {plan.max_domains === 0 ? 'Unlimited' : (plan.max_domains || 'N/A')}</div>
                          <div><strong>Emails:</strong> {plan.max_email_accounts === 0 ? 'Unlimited' : (plan.max_email_accounts || 'N/A')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary & Checkout */}
          <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none rounded-2xl"></div>
            <div className="relative z-10">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Domain Transfer: {transferData.domainName}</span>
                  <span className="font-semibold text-gray-900">${transferData.transferPrice.toFixed(2)}</span>
                </div>
                
                {selectedPlan !== 'domain-only' && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">
                      {hostingPlans.find((p: any) => p.id === selectedPlan)?.name} (First Month)
                    </span>
                    <span className="font-semibold text-gray-900">
                      ${parseFloat(hostingPlans.find((p: any) => p.id === selectedPlan)?.price_monthly || hostingPlans.find((p: any) => p.id === selectedPlan)?.price || '0').toFixed(2)}
                    </span>
                  </div>
                )}
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
                      ${calculateTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleContinueToCheckout}
                className="w-full bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
                <span className="relative z-10 flex items-center justify-center">
                  <FaShoppingCart className="mr-3" />
                  Continue to Checkout
                  <FaArrowRight className="ml-3" />
                </span>
              </button>
              
              <p className="text-center text-sm text-gray-600 mt-4">
                Secure checkout with SSL encryption
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DomainTransferHosting;